import { Check, Minus } from "lucide-react";
import { ReactNode } from "react";
import {
  Checkbox as AriaCheckbox,
  CheckboxGroup as AriaCheckboxGroup,
  CheckboxGroupProps as AriaCheckboxGroupProps,
  CheckboxProps as AriaCheckboxProps,
  CheckboxRenderProps,
  composeRenderProps,
  ValidationResult,
} from "react-aria-components";
import { composeTailwindRenderProps } from "../../utils";
import { Description, FieldError, Label } from "../Field";

/**
 * CheckboxGroup
 *
 * Groups multiple checkboxes with shared label/description.
 */
export interface CheckboxGroupProps extends Omit<AriaCheckboxGroupProps, "children"> {
  label?: string;
  children?: ReactNode;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
}

/**
 * Checkbox
 *
 * Single checkbox with Edges visuals.
 */
export interface CheckboxProps extends Omit<AriaCheckboxProps, "children"> {
  children?: ReactNode | ((props: CheckboxRenderProps) => ReactNode);
  /**
   * Visual variant of the checkbox
   * @default "default"
   */
  variant?: "default" | "brand";
}

export function CheckboxGroup(props: CheckboxGroupProps) {
  return (
    <AriaCheckboxGroup {...props} className={composeTailwindRenderProps(props.className, "flex flex-col gap-2")}>
      <Label>{props.label}</Label>
      {props.children}
      {props.description && <Description>{props.description}</Description>}
      <FieldError>{typeof props.errorMessage === "function" ? undefined : props.errorMessage}</FieldError>
    </AriaCheckboxGroup>
  );
}

function getCheckboxStyles({ isDisabled }: { isDisabled?: boolean }) {
  return `flex gap-2 items-center group text-sm transition ${isDisabled ? "text-text-disabled" : "text-text-caption"}`;
}

function getBoxStyles({
  isSelected,
  isInvalid,
  isDisabled,
  isIndeterminate,
  variant = "default",
}: {
  isSelected?: boolean;
  isInvalid?: boolean;
  isDisabled?: boolean;
  isIndeterminate?: boolean;
  variant?: "default" | "brand";
}) {
  const baseStyles =
    "w-5 h-5 flex-shrink-0 rounded-[var(--control-border-radius)] flex items-center justify-center border-2 transition ring-offset-2 focus:ring-2";

  const focusRingColor = variant === "brand" ? "focus:ring-action-brand" : "focus:ring-action-default";

  let colorStyles = "";
  if (isDisabled) {
    colorStyles = "border-border-muted bg-background-muted";
  } else if (isInvalid) {
    colorStyles = "border-feedback-error-border bg-feedback-error-background";
  } else if (isSelected || isIndeterminate) {
    const actionColor = variant === "brand" ? "action-brand" : "action-default";
    colorStyles = `bg-${actionColor} border-${actionColor} hover:bg-${actionColor}-hover hover:border-${actionColor}-hover`;
  } else {
    colorStyles = "bg-background-input border-border-input hover:border-border-focus";
  }

  return `${baseStyles} ${focusRingColor} ${colorStyles}`;
}

const iconStyles = "w-4 h-4 text-text-onPrimary group-disabled:text-text-disabled";

export function Checkbox(props: CheckboxProps) {
  const { children, variant = "default", ...otherProps } = props;
  return (
    <AriaCheckbox
      {...otherProps}
      className={composeRenderProps(
        props.className,
        (className, renderProps) => `${getCheckboxStyles(renderProps)} ${className || ""}`
      )}
    >
      {(renderProps) => {
        const { isSelected, isIndeterminate, ...rest } = renderProps;
        const checkboxContent = (
          <div
            className={getBoxStyles({
              isSelected,
              isIndeterminate,
              variant,
              ...rest,
            })}
          >
            {isIndeterminate ? (
              <Minus aria-hidden className={iconStyles} />
            ) : isSelected ? (
              <Check aria-hidden className={iconStyles} />
            ) : null}
          </div>
        );

        return (
          <>
            {checkboxContent}
            {typeof children === "function" ? children(renderProps) : children}
          </>
        );
      }}
    </AriaCheckbox>
  );
}
